import dynamic from 'next/dynamic';
import gql from 'graphql-tag';
import { print } from 'graphql';
import { fetchAPI } from '..';

import * as Hero from './Hero/schema';
import * as Articles from './Articles/schema';
import * as StaggeredBlocks from './StaggeredBlocks/schema';
import * as Gallery from './Gallery/schema';
import * as GalleryBlock from './GalleryBlock/schema';
import * as ImageBlock from './ImageBlock/schema';
import * as ItemList from './ItemList/schema';
import * as Cta from './Cta/schema';
import * as Text from './Text/schema';
import * as TextBlock from './TextBlock/schema';
import * as People from './People/schema';
import * as Documents from './Documents/schema';
import * as SplitPageSection from './SplitPageSection/schema';
import * as Timeline from './Timeline/schema';
import * as ColumnSection from './ColumnSection/schema';
import * as Infographic from './Infographic/schema';
import * as Infographics from './Infographics/schema';
import * as Quote from './Quote/schema';
import * as Buttons from './Buttons/schema';
import * as Offices from './Offices/schema';
import * as Form from './Form/schema';
import * as ContactBlock from './ContactBlock/schema';
import * as NewsletterSignup from './NewsletterSignup/schema';
import * as MediaList from './MediaList/schema';
import * as Divider from './Divider/schema';
import * as CookiePolicy from './CookiePolicy/schema';
import * as Variant from './Variant/schema';
import * as Stories from './Stories/schema';
import * as Faq from './Faq/schema';
import * as Video from './Video/schema';
import * as VideoList from './VideoList/schema';
import * as JobListing from './JobListing/schema';
import * as GalleryGrid from './GalleryGrid/schema';
import * as Stats from './Stats/schema';
import * as Table from './Table/schema';
import * as Events from './Events/schema';
import * as IndexGrid from './IndexGrid/schema';
import * as Catalog from './Catalog/schema';
import * as CatalogAlmaject from './CatalogAlmaject/schema';
import * as CatalogBranded from './CatalogBranded/schema';
import * as CatalogAlmatica from './CatalogAlmatica/schema';
import * as CatalogIceland from './CatalogIceland/schema';
import * as CatalogSimple from './CatalogSimple/schema';
import * as CatalogBasic from './CatalogBasic/schema';
import * as Packages from './Packages/schema';
import * as Banner from './Banner/schema';
import * as Retailers from './Retailers/schema';
import * as Sales from './Sales/schema';
import * as Quarters from './Quarters/schema';
import * as Shareholder from './Shareholder/schema';
import * as ArticleIndex from './ArticleIndex/schema';
import * as Pipeline from './Pipeline/schema';
import * as Related from './Related/schema';
import * as Accordion from './Accordion/schema';
import * as Portal from './Portal/schema';
import * as QuoteBlock from './QuoteBlock/schema';
import * as HeroButtons from './HeroButtons/schema';
import * as SmallPrint from './SmallPrint/schema';
import * as PageShortcuts from './PageShortcuts/schema';

export enum PageTypes {
  Home = 'Home',
  Page = 'Page',
  SplitPage = 'Split_page',
  Article = 'Article',
  Newsroom = 'Newsroom',
  Product = 'Product',
  ProductIceland = 'Product_iceland',
  SimpleProduct = 'Simple_product',
  BasicProduct = 'Basic_product',
  Story = 'Story',
  Event = 'Event',
}

export const components = {
  accordion: dynamic(() => import('./Accordion')),
  article_index: dynamic(() => import('./ArticleIndex')),
  articles: dynamic(() => import('./Articles')),
  banner: dynamic(() => import('./Banner')),
  buttons: dynamic(() => import('./Buttons')),
  catalog_almaject: dynamic(() => import('./CatalogAlmaject')),
  catalog_almatica: dynamic(() => import('./CatalogAlmatica')),
  catalog_basic: dynamic(() => import('./CatalogBasic')),
  catalog_branded: dynamic(() => import('./CatalogBranded')),
  catalog_iceland: dynamic(() => import('./CatalogIceland')),
  catalog_simple: dynamic(() => import('./CatalogSimple')),
  catalog: dynamic(() => import('./Catalog')),
  column_section: dynamic(() => import('./ColumnSection')),
  contact_block: dynamic(() => import('./ContactBlock')),
  content: dynamic(() => import('./StaggeredBlocks')),
  cookie_policy: dynamic(() => import('./CookiePolicy')),
  cta: dynamic(() => import('./Cta')),
  divider: dynamic(() => import('./Divider')),
  documents: dynamic(() => import('./Documents')),
  events: dynamic(() => import('./Events')),
  faq: dynamic(() => import('./Faq')),
  form: dynamic(() => import('./Form')),
  gallery_block: dynamic(() => import('./GalleryBlock')),
  gallery_grid: dynamic(() => import('./GalleryGrid')),
  gallery: dynamic(() => import('./Gallery')),
  hero_buttons: dynamic(() => import('./HeroButtons')),
  hero: dynamic(() => import('./Hero')),
  image_block: dynamic(() => import('./ImageBlock')),
  infographic: dynamic(() => import('./Infographic')),
  infographics: dynamic(() => import('./Infographics')),
  item_list: dynamic(() => import('./ItemList')),
  job_listing: dynamic(() => import('./JobListing')),
  media_list: dynamic(() => import('./MediaList')),
  newsletter_signup: dynamic(() => import('./NewsletterSignup')),
  offices: dynamic(() => import('./Offices')),
  packages: dynamic(() => import('./Packages')),
  page_shortcuts: dynamic(() => import('./PageShortcuts')),
  people: dynamic(() => import('./People')),
  pipeline: dynamic(() => import('./Pipeline')),
  portal: dynamic(() => import('./Portal')),
  product_index: dynamic(() => import('./IndexGrid')),
  quarterly_reports: dynamic(() => import('./Quarters')),
  quote_block: dynamic(() => import('./QuoteBlock')),
  quote: dynamic(() => import('./Quote')),
  related: dynamic(() => import('./Related')),
  retailers: dynamic(() => import('./Retailers')),
  sales: dynamic(() => import('./Sales')),
  shareholder_information: dynamic(() => import('./Shareholder')),
  small_print: dynamic(() => import('./SmallPrint')),
  stats: dynamic(() => import('./Stats')),
  stories: dynamic(() => import('./Stories')),
  table: dynamic(() => import('./Table')),
  text_block: dynamic(() => import('./TextBlock')),
  text: dynamic(() => import('./Text')),
  timeline: dynamic(() => import('./Timeline')),
  variant: dynamic(() => import('./Variant')),
  video_list: dynamic(() => import('./VideoList')),
  video: dynamic(() => import('./Video')),
};

export type PageTypesWithSlices =
  | PageTypes.Home
  | PageTypes.Page
  | PageTypes.SplitPage
  | PageTypes.Article
  | PageTypes.Newsroom
  | PageTypes.Product
  | PageTypes.ProductIceland
  | PageTypes.BasicProduct
  | PageTypes.Story
  | PageTypes.Event;

export enum SliceTypes {
  Articles = 'articles',
  Buttons = 'buttons',
  ColumnSection = 'column_section',
  ContactBlock = 'contact_block',
  CookiePolicy = 'cookie_policy',
  Cta = 'cta',
  Divider = 'divider',
  Documents = 'documents',
  Faq = 'faq',
  Form = 'form',
  Gallery = 'gallery',
  GalleryBlock = 'gallery_block',
  GalleryGrid = 'gallery_grid',
  Hero = 'hero',
  ImageBlock = 'image_block',
  Infographic = 'infographic',
  Infographics = 'infographics',
  ItemList = 'item_list',
  JobListing = 'job_listing',
  MediaList = 'media_list',
  NewsletterSignup = 'newsletter_signup',
  Offices = 'offices',
  People = 'people',
  Quote = 'quote',
  SplitPageSection = 'split_page_section',
  StaggeredBlocks = 'content',
  Stories = 'stories',
  Text = 'text',
  TextBlock = 'text_block',
  Timeline = 'timeline',
  Variant = 'variant',
  Video = 'video',
  VideoList = 'video_list',
  Stats = 'stats',
  Table = 'table',
  Data = 'data',
  IndexGrid = 'product_index',
  Catalog = 'catalog',
  CatalogAlmaject = 'catalog_almaject',
  CatalogBranded = 'catalog_branded',
  CatalogAlmatica = 'catalog_almatica',
  CatalogIceland = 'catalog_iceland',
  CatalogSimple = 'catalog_simple',
  CatalogBasic = 'catalog_basic',
  Packages = 'packages',
  Banner = 'banner',
  Retailers = 'retailers',
  Sales = 'sales',
  Quarters = 'quarterly_reports',
  Shareholder = 'shareholder_information',
  ArticleIndex = 'article_index',
  Events = 'events',
  Pipeline = 'pipeline',
  Related = 'related',
  Accordion = 'accordion',
  Portal = 'portal',
  QuoteBlock = 'quote_block',
  HeroButtons = 'hero_buttons',
  SmallPrint = 'small_print',
  PageShortcuts = 'page_shortcuts',
}

export interface IBaseSlice {
  type: SliceTypes;
  title?: string;
}

const sliceDefinitions = {
  [SliceTypes.Articles]: Articles,
  [SliceTypes.Buttons]: Buttons,
  [SliceTypes.ColumnSection]: ColumnSection,
  [SliceTypes.ContactBlock]: ContactBlock,
  [SliceTypes.CookiePolicy]: CookiePolicy,
  [SliceTypes.Cta]: Cta,
  [SliceTypes.Divider]: Divider,
  [SliceTypes.Documents]: Documents,
  [SliceTypes.Faq]: Faq,
  [SliceTypes.Form]: Form,
  [SliceTypes.Gallery]: Gallery,
  [SliceTypes.GalleryBlock]: GalleryBlock,
  [SliceTypes.GalleryGrid]: GalleryGrid,
  [SliceTypes.Hero]: Hero,
  [SliceTypes.ImageBlock]: ImageBlock,
  [SliceTypes.Infographic]: Infographic,
  [SliceTypes.Infographics]: Infographics,
  [SliceTypes.ItemList]: ItemList,
  [SliceTypes.JobListing]: JobListing,
  [SliceTypes.MediaList]: MediaList,
  [SliceTypes.NewsletterSignup]: NewsletterSignup,
  [SliceTypes.Offices]: Offices,
  [SliceTypes.People]: People,
  [SliceTypes.Quote]: Quote,
  [SliceTypes.SplitPageSection]: SplitPageSection,
  [SliceTypes.StaggeredBlocks]: StaggeredBlocks,
  [SliceTypes.Stories]: Stories,
  [SliceTypes.Text]: Text,
  [SliceTypes.TextBlock]: TextBlock,
  [SliceTypes.Timeline]: Timeline,
  [SliceTypes.Variant]: Variant,
  [SliceTypes.Video]: Video,
  [SliceTypes.VideoList]: VideoList,
  [SliceTypes.Stats]: Stats,
  [SliceTypes.Table]: Table,
  [SliceTypes.Events]: Events,
  [SliceTypes.IndexGrid]: IndexGrid,
  [SliceTypes.Catalog]: Catalog,
  [SliceTypes.CatalogAlmaject]: CatalogAlmaject,
  [SliceTypes.CatalogAlmatica]: CatalogAlmatica,
  [SliceTypes.CatalogBranded]: CatalogBranded,
  [SliceTypes.CatalogIceland]: CatalogIceland,
  [SliceTypes.CatalogSimple]: CatalogSimple,
  [SliceTypes.CatalogBasic]: CatalogBasic,
  [SliceTypes.Packages]: Packages,
  [SliceTypes.Banner]: Banner,
  [SliceTypes.Retailers]: Retailers,
  [SliceTypes.Sales]: Sales,
  [SliceTypes.Quarters]: Quarters,
  [SliceTypes.Shareholder]: Shareholder,
  [SliceTypes.ArticleIndex]: ArticleIndex,
  [SliceTypes.Pipeline]: Pipeline,
  [SliceTypes.Related]: Related,
  [SliceTypes.Accordion]: Accordion,
  [SliceTypes.Portal]: Portal,
  [SliceTypes.QuoteBlock]: QuoteBlock,
  [SliceTypes.HeroButtons]: HeroButtons,
  [SliceTypes.SmallPrint]: SmallPrint,
  [SliceTypes.PageShortcuts]: PageShortcuts,
};

const capitalizeFirstLetter = (str: string) => `${str.charAt(0).toUpperCase()}${str.slice(1)}`;

const createFragments = ({ fragmentName, typeWithBody, availableSlices, type }) => {
  return gql`
    fragment ${fragmentName} on ${typeWithBody} {
      ... on ${typeWithBody} {
        ${availableSlices.map((slice: SliceTypes) => `...${capitalizeFirstLetter(slice) + type}`)}
      }
    }

    ${availableSlices
      .filter((slice: SliceTypes) => {
        return sliceDefinitions[slice] && sliceDefinitions[slice].fragment;
      })
      .map((slice: SliceTypes) => {
        let fragmentFunction = sliceDefinitions[slice]?.fragment;

        // Custom fragments
        if (PageTypes.Article == type && slice === SliceTypes.Video) {
          fragmentFunction = sliceDefinitions[slice]?.customArticleFragment;
        }

        if (PageTypes.Article == type && slice === SliceTypes.Documents) {
          fragmentFunction = sliceDefinitions[slice]?.customSplitPageFragment;
        }

        if (PageTypes.Page == type && slice === SliceTypes.Text) {
          fragmentFunction = sliceDefinitions[slice]?.customPageFragment;
        }

        if (PageTypes.SplitPage == type && slice === SliceTypes.Text) {
          fragmentFunction = sliceDefinitions[slice]?.customSplitPageFragment;
        }

        if (PageTypes.SplitPage == type && slice === SliceTypes.Documents) {
          fragmentFunction = sliceDefinitions[slice]?.customSplitPageFragment;
        }

        if (PageTypes.SplitPage == type && slice === SliceTypes.Form) {
          fragmentFunction = sliceDefinitions[slice]?.customSplitPageFragment;
        }

        return print(
          fragmentFunction(
            capitalizeFirstLetter(slice),
            typeWithBody,
            capitalizeFirstLetter(slice) + type
          )
        );
      })
      .join('')}
  `;
};

export const generateSliceFragments = async (
  type: PageTypesWithSlices,
  fragmentName: string = 'Slices'
) => {
  try {
    // Often the object containing the slices is named 'Body1' for the Home document type.
    // That's because the site’s navigation is usually set up with slices in the same document type.
    const body = 'Body';
    // const body = type === PageTypes.Home ? 'Body1' : 'Body';

    const typeWithBody = `${type}${body}`;
    const query = gql`
      {
        ${Object.keys(sliceDefinitions).map(
          (sliceType: SliceTypes) => `
          ${sliceType}: __type(name: "${type}${body}${capitalizeFirstLetter(sliceType)}") {
            name
          }
        `
        )}
      }
    `;
    const res = await fetchAPI(print(query));
    const availableSlices = Object.keys(res).filter((x) => res[x] !== null);

    // console.log('availableSlices', availableSlices);

    const out = createFragments({
      fragmentName,
      type,
      typeWithBody,
      availableSlices,
    });

    // console.log('out', print(out));

    return out;
  } catch (err) {
    console.log('err', err);
    return null;
  }
};

// TODO: always use generateSliceFragmentsPages, just need to cleanup the code a little bit
export const generateSliceFragmentsPages = async (
  type: PageTypesWithSlices,
  fragmentName: string = 'Slices'
) => {
  try {
    // Often the object containing the slices is named 'Body1' for the Home document type.
    // That's because the site’s navigation is usually set up with slices in the same document type.
    const body = 'Body';
    // const body = type === PageTypes.Home ? 'Body1' : 'Body';

    const typeWithBody = `${type}${body}`;
    const query = gql`
      {
        ${Object.keys(sliceDefinitions).map(
          (sliceType: SliceTypes) => `
          ${sliceType}: __type(name: "${type}${body}${capitalizeFirstLetter(sliceType)}") {
            name
          }
        `
        )}
      }
    `;
    const res = await fetchAPI(print(query));
    const availableSlices = Object.keys(res).filter((x) => res[x] !== null);

    const PAGE_SIZE = 20;

    const pages = new Array(Math.ceil(availableSlices.length / PAGE_SIZE))
      .fill(null)
      .map((_, i) => {
        const fragments = createFragments({
          fragmentName,
          type,
          typeWithBody,
          availableSlices: availableSlices.slice(i * PAGE_SIZE, i * PAGE_SIZE + PAGE_SIZE),
        });

        // console.log(slices);
        // console.log(print(fragments));

        return fragments;
      });

    return pages;
  } catch (err) {
    console.log('err', err);
    return null;
  }
};

const fetchAndParseNestedData = async (slice: any, lang, previewData, redirects) => {
  const defaultOptions = { lang, previewData, redirects, limit: null };

  switch (slice.type) {
    case SliceTypes.JobListing:
      return JobListing.fetchJobsAndParse(defaultOptions);
    case SliceTypes.Stories:
      return Stories.fetchStoriesAndParse(defaultOptions);
    case SliceTypes.CatalogBasic:
      return CatalogBasic.fetchBasicProductsAndParse(defaultOptions);
    case SliceTypes.CatalogIceland:
      return CatalogIceland.fetchProductsIcelandAndParse(defaultOptions);
    case SliceTypes.CatalogSimple:
      return CatalogSimple.fetchSimpleProductsAndParse(defaultOptions);
    case SliceTypes.Form:
      return Form.fetchForm(slice);
    case SliceTypes.Catalog:
      return Catalog.fetchProductsAndParse(defaultOptions);
    case SliceTypes.CatalogBranded:
      return Catalog.fetchProductsAndParse(defaultOptions);
    case SliceTypes.CatalogAlmaject:
      return CatalogAlmaject.fetchProductsAndParse(defaultOptions);
    case SliceTypes.IndexGrid:
      return IndexGrid.fetchBasicProductsAndParse({
        ...defaultOptions,
        limit: 500,
        tags: slice.categories || undefined,
      });
    case SliceTypes.Sales:
      return Sales.fetchInvestorInformationsAndParse(defaultOptions);
    case SliceTypes.Quarters:
      return Quarters.fetchInvestorInformationsAndParse(defaultOptions);
    case SliceTypes.Shareholder:
      return Shareholder.fetchInvestorInformationsAndParse(defaultOptions);
    case SliceTypes.Articles:
      return Articles.fetchArticlesAndParse({
        ...defaultOptions,
        limit: 3,
        tags: slice.queryAll ? slice.tags : undefined,
        tagsIn: !slice.queryAll ? slice.tags : undefined,
      });
    case SliceTypes.ArticleIndex:
      return ArticleIndex.fetchArticlesAndParse({
        ...defaultOptions,
        tags: slice.queryAll ? slice.tags : undefined,
        tagsIn: !slice.queryAll ? slice.tags : undefined,
        lang,
      });
    case SliceTypes.Events:
      return Events.fetchEventsAndParse(defaultOptions);
    default:
      return null;
  }
};

export const parseSlices = async (
  slices: Array<{
    type: string;
    primary?: any;
    fields?: any[];
  }>,
  lang: string,
  previewData: any,
  redirects?: Array<{ from: string; to: string }>
): Promise<IBaseSlice[]> => {
  const slicesData = slices
    .filter((slice) => slice.type) // prismic graphql somtimes returns empty slice objects
    .map((slice) => {
      const sliceType = sliceDefinitions[slice.type];
      if (!sliceType) {
        console.warn(`No definition found for slice '${slice.type}'`);
        return { type: slice.type };
      }
      return sliceType.parse(slice, redirects || [], lang);
    });

  // Check if we need to fetch nested data
  return await Promise.all(
    slicesData.map(async (slice) => {
      const items = await fetchAndParseNestedData(slice, lang, previewData, redirects);

      return {
        ...slice,
        ...(items && { items }),
      };
    })
  );
};
